/**
 * Os 11 estados obrigatórios de tela (03 §3.5 tabela A / R052).
 *
 * Por que isto existe como UM arquivo: se cada tela inventar seu próprio texto de "sem internet", o
 * funcionário aprende onze frases diferentes para a mesma situação — e alguma tela vai esquecer de
 * dizer que o trabalho está salvo. Aqui o texto é único; a tela só diz QUAIS estados se aplicam e,
 * quando precisar, sobrescreve com justificativa.
 *
 * Regra do contrato que este arquivo carrega: **em nenhum estado dados locais são descartados.**
 */
export type EstadoTela =
  | 'carregando'
  | 'vazio'
  | 'sem_internet'
  | 'tiny_indisponivel'
  | 'sem_permissao'
  | 'versao_incompativel'
  | 'acao_em_andamento'
  | 'falha_recuperavel'
  | 'conflito'
  | 'sucesso_parcial'
  | 'sucesso_completo';

export interface DefinicaoEstado {
  /** O que o funcionário lê. Linguagem de operação, nunca jargão técnico. */
  texto: string;
  /** O que ele pode fazer agora. `null` = só aguardar. */
  acao: string | null;
  /** O que acontece com o que ele já fez. Nunca é "perdido". */
  dadosLocais: string;
  /** Peso visual: informação, atenção ou problema. */
  tom: 'neutro' | 'atencao' | 'erro' | 'sucesso';
}

export const ESTADOS: Record<EstadoTela, DefinicaoEstado> = {
  carregando: {
    texto: 'Carregando…',
    acao: null,
    dadosLocais: 'intactos',
    tom: 'neutro',
  },
  vazio: {
    texto: 'Nada por aqui ainda',
    acao: 'Criar',
    dadosLocais: 'intactos',
    tom: 'neutro',
  },
  sem_internet: {
    texto: 'Sem internet — tudo é salvo neste aparelho',
    acao: 'Ver o que está só aqui',
    dadosLocais: 'gravados no aparelho; a fila sobe sozinha quando a internet voltar',
    tom: 'atencao',
  },
  tiny_indisponivel: {
    texto: 'Tiny fora do ar — a operação continua; validamos depois',
    acao: 'Continuar',
    dadosLocais: 'gravados como provisório, aguardando validação do Tiny',
    tom: 'atencao',
  },
  sem_permissao: {
    texto: 'Você não tem permissão para isso — peça a um gestor',
    acao: 'Voltar',
    dadosLocais: 'nada foi gravado',
    tom: 'erro',
  },
  versao_incompativel: {
    texto: 'Atualize o aplicativo para continuar',
    acao: 'Atualizar',
    dadosLocais: 'a leitura local e o envio da fila continuam funcionando',
    tom: 'atencao',
  },
  acao_em_andamento: {
    texto: 'Fazendo…',
    acao: null,
    dadosLocais: 'já gravados no aparelho antes de aparecer na tela',
    tom: 'neutro',
  },
  falha_recuperavel: {
    texto: 'Não deu certo agora — vamos tentar de novo',
    acao: 'Tentar de novo',
    dadosLocais: 'gravados; a fila tenta sozinha',
    tom: 'erro',
  },
  conflito: {
    texto:
      'Outra pessoa alterou este campo; as duas versões estão guardadas — o gestor vai decidir. Você pode continuar',
    acao: 'Ver as duas versões',
    dadosLocais: 'as duas versões preservadas',
    tom: 'atencao',
  },
  sucesso_parcial: {
    texto: 'Concluído neste aparelho — ainda falta subir',
    acao: 'Ver pendências',
    dadosLocais: 'a fila esvazia sozinha',
    tom: 'sucesso',
  },
  sucesso_completo: {
    texto: 'Sincronizado e processado',
    acao: 'Continuar',
    dadosLocais: 'tudo no servidor',
    tom: 'sucesso',
  },
};

/** Monta o texto final: o padrão, ou o específico da tela (que precisa de justificativa no código). */
export function textoDoEstado(
  estado: EstadoTela,
  especifico?: { texto?: string; acao?: string | null; porque?: string },
): DefinicaoEstado {
  const padrao = ESTADOS[estado];
  if (!especifico) return padrao;
  return {
    ...padrao,
    ...(especifico.texto !== undefined ? { texto: especifico.texto } : {}),
    ...(especifico.acao !== undefined ? { acao: especifico.acao } : {}),
  };
}
